import { useState, useEffect, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import localeUtils from 'utils/LanguageLocale';
import cookieUtils from 'utils/Cookies';
import RmnAndPlaUtils from 'utils/rmnAndPla';
import RMN_BANNER_TYPES from 'components/Rmn/constants';
import isRmnCombinedCallFeatureEnabled from 'components/Rmn/utils';
import ComponentResponsiveChangeCheck from 'utils/ComponentResponsiveChangeCheck';
import deepEqual from 'deep-equal';
import { CHANEL } from 'constants/brands';

const { TYPES, MOBILE_VIEW, LEADERBOARD_POSITION_INDEX, BANNER_AND_PLA_TYPES } = RMN_BANNER_TYPES;

const useRmnBanners = () => {
    const currentProduct = useSelector(state => state.page.product, shallowEqual);
    const rmnBanners = useSelector(state => state.rmnBanners, shallowEqual);

    const [rmnMidPageBannerPayload, setRmnMidPageBannerPayload] = useState({});
    const [fallbackBannerPayload, setFallbackBannerPayload] = useState(undefined);
    const [showSkeleton, setShowSkeleton] = useState(false);

    const rmnBannersRef = useRef(rmnBanners);
    const currentProductRef = useRef(currentProduct);
    const isInitialRef = useRef(true);

    useEffect(() => {
        rmnBannersRef.current = rmnBanners;
    }, [rmnBanners]);
    useEffect(() => {
        currentProductRef.current = currentProduct;
    }, [currentProduct]);

    const handleFallback = slot => {
        setShowSkeleton(true);
        const banners = rmnBannersRef.current || {};
        const bannersSlot = banners[slot];
        const fallbackBanner = bannersSlot?.fallback || {};

        setFallbackBannerPayload(prev => {
            if (!deepEqual(fallbackBanner, prev)) {
                return fallbackBanner;
            }

            return prev;
        });
        setShowSkeleton(false);
    };

    const requestPageBannersAndPlas = async isMobile => {
        const product = currentProductRef.current;
        const maxProducts = 1;
        const slotPrefix = localeUtils.isUS() ? '25' : '26';
        const { productDetails = {}, currentSku = {} } = product || {};
        // eslint-disable-next-line camelcase
        const targets = { match_products: [productDetails.productId] };

        const slot = isMobile ? `${slotPrefix}04212` : `${slotPrefix}04112`;
        const type = isMobile ? TYPES.MOBILE_LEADERBOARD.NAME : TYPES.SUPER_LEADERBOARD.NAME;
        const section = RMN_BANNER_TYPES.SECTIONS.MAIN;

        const plaSponsoredProductOpts = {
            targets,
            slot,
            count: maxProducts,
            // eslint-disable-next-line camelcase
            count_fill: maxProducts,
            shouldDisplayPlas: !!cookieUtils.read(cookieUtils.KEYS.SEPH_SESSION)
        };

        const requestParams = RmnAndPlaUtils.mountRequestParams({
            targets,
            slot,
            type,
            section,
            contextId: productDetails.productId,
            count: maxProducts,
            skuId: currentSku?.skuId || '',
            skuPrice: currentSku?.salePrice || currentSku?.listPrice || '',
            skuAvailability: Number(!currentSku?.isOutOfStock)
        });

        setShowSkeleton(true);

        RmnAndPlaUtils.initializeBannersAndPlas({
            ...requestParams,
            isMobile,
            plaSponsoredProductOpts
        })
            .then(response => {
                const bannerData = response?.[BANNER_AND_PLA_TYPES.LEADERBOARD]?.[LEADERBOARD_POSITION_INDEX.TOP];
                const hasBannerData = Boolean(bannerData);

                setRmnMidPageBannerPayload(hasBannerData ? bannerData : {});
                setShowSkeleton(false);

                if (!hasBannerData) {
                    handleFallback(slot);
                }
            })
            .catch(() => {
                handleFallback(slot);
            });
    };

    useEffect(() => {
        if (!isRmnCombinedCallFeatureEnabled()) {
            return;
        }

        const brandName = currentProduct?.productDetails?.brand?.displayName?.toLowerCase() || '';

        if (brandName !== CHANEL) {
            // eslint-disable-next-line no-new
            new ComponentResponsiveChangeCheck(MOBILE_VIEW, requestPageBannersAndPlas);
            RmnAndPlaUtils.resetBanners();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialRef.current) {
            isInitialRef.current = false;

            return;
        }

        if (isRmnCombinedCallFeatureEnabled()) {
            requestPageBannersAndPlas(Sephora.isMobile());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProduct?.currentSku?.skuId, currentProduct?.currentSku?.replenishmentFreqNum, currentProduct?.currentSku?.replenishmentFreqType]);

    return { rmnMidPageBannerPayload, fallbackBannerPayload, showSkeleton };
};

export default useRmnBanners;
